import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Trans } from '@/components'
import { Deployment } from '@/models/deployment.ts'
import { DeploymentBill } from '@/models/deploymentBill.ts'
import { InstanceStatus } from '@/models/instance.ts'
import { formatDuration, formatMillicentPrice, formatPrice } from '@/utils/formatter.ts'

interface Props {
  _deployment: Deployment
  _deploymentBills: Map<string, DeploymentBill[]>
}

export default function DeploymentBilling({ _deployment, _deploymentBills }: Props) {
  const { t } = useTranslation('autoDeploy')
  const [deploymentBills, setDeploymentBills] = useState<DeploymentBill[]>([])

  useEffect(() => {
    if (_deployment && _deploymentBills) {
      const bills = _deploymentBills.get(_deployment.resID) || []
      setDeploymentBills(bills)
    }
  }, [_deployment, _deploymentBills])

  const renderInstancesStatus = useCallback(
    (instanceStatus: number) => {
      const statusConfig = new Map<number, { text: string; color: string }>([
        [InstanceStatus.Canceling, { text: t('instanceStatus.idle'), color: 'text-warning' }],
        [InstanceStatus.Pending, { text: t('instanceStatus.idle'), color: 'text-warning' }],
        [InstanceStatus.Running, { text: t('instanceStatus.running'), color: 'text-success' }],
      ])

      const defaultStatus = { text: t('instanceStatus.standby'), color: 'text-warning' }
      const { text, color } = statusConfig.get(instanceStatus) || defaultStatus

      return <div className={color}>{text}</div>
    },
    [t],
  )

  return (
    <div className="bg-panel-gray-100 rounded-lg flex flex-wrap t-sm text-tertiary gap-y-4">
      <div className="w-full grid grid-cols-5 gap-y-4 bg-panel-gray-100 rounded-lg">
        {deploymentBills.length > 0 ? (
          deploymentBills.map((bill, index) => (
            <div key={index} className="col-span-5 grid grid-cols-5 space-x-4 gap-y-2">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployBilling.tableHeaderStartTime" ns="autoDeploy">
                    Start Time
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {dayjs.unix(bill.createTime.seconds).format('YYYY-MM-DD HH:mm:ss')}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployBilling.tableHeaderEndTime" ns="autoDeploy">
                    End Time
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {bill.instStatus === InstanceStatus.Running ? (
                    renderInstancesStatus(bill.instStatus)
                  ) : bill.finishTime?.seconds === 0 ? (
                    <>-</>
                  ) : (
                    <>
                      {bill.finishTime?.seconds
                        ? dayjs.unix(bill.finishTime.seconds).format('YYYY-MM-DD HH:mm:ss')
                        : t('common.notAvailable')}
                    </>
                  )}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployBilling.tableHeaderPrice" ns="autoDeploy">
                    Price
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatMillicentPrice(
                    bill.priceCent || 0,
                    false,
                    bill.priceCent > 100 ? t('common.units.credits') : t('common.units.credit'),
                  )}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployBilling.tableHeaderCost" ns="autoDeploy">
                    Cost
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatPrice(
                    Math.abs(bill.costCent) || 0,
                    false,
                    Math.abs(bill.costCent) > 100
                      ? t('common.units.credits')
                      : t('common.units.credit'),
                  )}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployBilling.tableHeaderDuration" ns="autoDeploy">
                    Duration
                  </Trans>
                </div>
                <div className="t-sb text-primary">{formatDuration(bill.runningDurationSec)}</div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-5 text-center text-secondary bg-neutral-50">
            <Trans i18nKey="deployBilling.noData" ns="autoDeploy">
              No billing data available
            </Trans>
          </div>
        )}
      </div>
    </div>
  )
}
